import { type Moment, moment } from "@msuite/utils/date";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { DraggedCrew } from "./dragged-crew";

type PlanMode = "daily" | "capacity";

export type PlanCapacityView = "manager" | "planner";

/** How many weeks the capacity board draws at once. */
export const PLAN_CAPACITY_WEEK_OPTIONS = [3, 4, 5, 6, 7, 8] as const;

export type PlanCapacityWeeks = (typeof PLAN_CAPACITY_WEEK_OPTIONS)[number];

interface IPlanStore {
	mode: PlanMode;
	setMode: (mode: PlanMode) => void;

	capacityView: PlanCapacityView;
	setCapacityView: (capacityView: PlanCapacityView) => void;

	capacityWeeks: PlanCapacityWeeks;
	setCapacityWeeks: (capacityWeeks: PlanCapacityWeeks) => void;

	currentWeek: Moment;
	nextWeek: () => void;
	previousWeek: () => void;
	resetWeek: () => void;

	activeDate: Moment;
	setActiveDate: (date: string) => void;

	showResources: boolean;
	toggleShowResources: () => void;

	isDragging: boolean;
	setIsDragging: (isDragging: boolean) => void;

	/**
	 * The crew card in the air over the capacity board, or null. Set by the one monitor the manager
	 * view mounts and read by every ticket row, which dims itself while a crew it could not take is
	 * being dragged. Never persisted: a drag does not survive a reload.
	 */
	draggedCrew: DraggedCrew | null;
	setDraggedCrew: (draggedCrew: DraggedCrew | null) => void;
}

export const usePlanStore = create<IPlanStore>()(
	persist(
		(set) => ({
			mode: "daily",
			/** Capacity planning is worked with the resources panel open — the crews it lists are
			 * what gets dragged onto the board — so entering that mode opens it. Closing it by hand
			 * holds for as long as the mode does. */
			setMode: (mode: PlanMode) =>
				set(mode === "capacity" ? { mode, showResources: true } : { mode }),

			capacityView: "manager",
			setCapacityView: (capacityView: PlanCapacityView) => set({ capacityView }),

			capacityWeeks: 3,
			setCapacityWeeks: (capacityWeeks: PlanCapacityWeeks) => set({ capacityWeeks }),

			currentWeek: moment(),
			nextWeek: () => set((state) => ({ currentWeek: state.currentWeek.clone().add(1, "week") })),
			previousWeek: () =>
				set((state) => ({ currentWeek: state.currentWeek.clone().subtract(1, "week") })),
			resetWeek: () => set({ currentWeek: moment() }),
			activeDate: moment(),
			setActiveDate: (date: string) => set({ activeDate: moment(date) }),
			showResources: false,
			toggleShowResources: () => set((state) => ({ showResources: !state.showResources })),

			isDragging: false,
			setIsDragging: (isDragging: boolean) => set(() => ({ isDragging })),

			draggedCrew: null,
			setDraggedCrew: (draggedCrew: DraggedCrew | null) => set(() => ({ draggedCrew })),
		}),
		{
			name: "msuite-plan-storage",
			/** The week and the active date are Moment instances, which JSON rehydrates as strings —
			 * every caller of clone() or add() would throw on one, so only the board selections are
			 * written. Those are plain values and survive the round trip. */
			partialize: (state) => ({
				mode: state.mode,
				capacityView: state.capacityView,
				capacityWeeks: state.capacityWeeks,
			}),
			/** The mode survives a reload, so the panel has to follow it out of storage too —
			 * rehydrating into capacity planning with the panel shut is the same board with its
			 * crews missing. */
			merge: (persisted, current) => {
				const state = { ...current, ...(persisted as Partial<IPlanStore>) };
				return { ...state, showResources: state.mode === "capacity" };
			},
			storage: createJSONStorage(() => localStorage),
		},
	),
);
